import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { ContentContainer } from '@/components/shell/ContentContainer';
import { CatalogWorldTile } from '@/components/patterns/CatalogWorldTile';
import { fetchCatalogWorlds } from '@/features/catalog/api';
import type { CatalogWorldSummary } from '@/features/catalog/types';
import { STRINGS } from '@/strings/strings';

/**
 * Catalog browse (Block D) — a grid of ALL published Wissenswelten (ownership-
 * agnostic, GET /api/catalog/worlds). Tile → the existing /catalog/:slug detail
 * (unlock + play/read). Cover images come back as pre-signed URLs on the world
 * summary; CatalogWorldTile shows the placeholder when the URL is null.
 * Build-flag gated in App.tsx; the route is absent when CATALOG_ENABLED is off.
 */
export function CatalogBrowseRoute() {
  const S = STRINGS.catalog.browse;
  const navigate = useNavigate();
  const [worlds, setWorlds] = useState<CatalogWorldSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    let active = true;
    void (async () => {
      setWorlds(null);
      setLoadError(null);
      try {
        const data = await fetchCatalogWorlds();
        if (active) setWorlds(data);
      } catch {
        if (active) setLoadError(S.loadFailed);
      }
    })();
    return () => {
      active = false;
    };
  }, [S.loadFailed]);

  return (
    <ContentContainer maxWidth="default">
      <h1 className="mb-1 text-[22px] font-semibold text-dream-navy">{S.heading}</h1>
      <p className="mb-5 text-sm text-dream-navy/70">{S.subline}</p>

      {loadError && <p className="text-sm text-[color:var(--color-error)]">{loadError}</p>}

      {!worlds && !loadError && (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-dream-navy/50" aria-hidden />
        </div>
      )}

      {worlds && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {worlds.map((w) => (
            <CatalogWorldTile
              key={w.slug}
              worldSlug={w.slug}
              title={w.title}
              coverSrc={w.coverImageUrl}
              onClick={() => navigate(`/catalog/${w.slug}`)}
              testId={`catalog-tile-${w.slug}`}
            />
          ))}
        </div>
      )}
    </ContentContainer>
  );
}
